import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Search, FileText, FolderOpen, Database, Clock,
  Loader2, ArrowRight, X, Command, CornerDownLeft,
  LayoutDashboard, Lightbulb, Shield, Handshake, History, TrendingUp,
  Calculator, Building2, Sparkles, Share2, BarChart3, Settings, Mail,
} from "lucide-react";
import { globalSearch } from "@/lib/api";

const RECENT_SEARCHES_KEY = "les_lex_recent_searches";
const MAX_RECENT = 5;

const typeConfig = {
  project: { icon: FolderOpen, label: "Projekty", color: "text-blue-600 bg-blue-50" },
  source: { icon: FileText, label: "Źródła", color: "text-amber-600 bg-amber-50" },
  knowledge: { icon: Database, label: "Baza wiedzy", color: "text-emerald-600 bg-emerald-50" },
};

// ============================================================================
// Command Bar — komendy nawigacyjne (ewolucja 2026-05-30).
// Additive: rozszerza istniejacy GlobalSearch o "Przejdz do" + szybkie akcje,
// bez ruszania logiki wyszukiwania. keywords[] = dopasowanie po wpisaniu frazy.
// ============================================================================
const NAV_COMMANDS = [
  { id: "dashboard", label: "Pulpit", path: "/dashboard", icon: LayoutDashboard, keywords: ["start", "dashboard", "glowna", "home"], popular: true },
  { id: "advice", label: "Porady prawne", path: "/advice", icon: Lightbulb, keywords: ["porada", "pytanie", "podatk", "doradz"], popular: true },
  { id: "contract", label: "Analiza umów", path: "/contract-analysis", icon: Shield, keywords: ["umowa", "kontrakt", "klauzul", "analiza"], popular: true },
  { id: "detective", label: "Mecenas Detektyw", path: "/mecenas-detektyw", icon: Sparkles, keywords: ["detektyw", "kontrahent", "sprawdz firme", "due diligence", "intel"], adminOnly: true },
  { id: "negotiation", label: "Negocjacje", path: "/negotiation", icon: Handshake, keywords: ["negocjacj", "ugoda"] },
  { id: "legal-search", label: "Wyszukiwarka prawa", path: "/legal-search", icon: Search, keywords: ["orzeczeni", "wyrok", "saos", "ustawa", "przepis"], popular: true },
  { id: "prediction", label: "Predykcja spraw", path: "/prediction", icon: TrendingUp, keywords: ["predykcja", "prognoza", "wynik sprawy"] },
  { id: "templates", label: "Szablony", path: "/templates", icon: FileText, keywords: ["szablon", "wzor", "formularz", "pismo"] },
  { id: "calculators", label: "Kalkulatory", path: "/calculators", icon: Calculator, keywords: ["kalkulator", "pit", "cit", "vat", "odsetki", "oplata"] },
  { id: "court-portal", label: "Portal sądowy", path: "/court-portal", icon: Building2, keywords: ["sad", "e-sad", "termin", "pismo procesowe"] },
  { id: "knowledge-base", label: "Baza wiedzy", path: "/knowledge-base", icon: Database, keywords: ["baza wiedzy", "kodeks", "kis"] },
  { id: "due-diligence", label: "Due Diligence", path: "/due-diligence", icon: Building2, keywords: ["dd", "due diligence", "audyt"] },
  { id: "compliance", label: "Compliance", path: "/compliance", icon: Shield, keywords: ["compliance", "rodo", "aml", "sankcje"] },
  { id: "knowledge-graph", label: "Graf wiedzy", path: "/knowledge-graph", icon: Share2, keywords: ["graf", "powiazania", "neo4j"] },
  { id: "analytics", label: "Analityka", path: "/analytics", icon: BarChart3, keywords: ["analityka", "statystyki", "raporty"] },
  { id: "inbox", label: "Skrzynka", path: "/inbox", icon: Mail, keywords: ["email", "skrzynka", "poczta", "inbox"] },
  { id: "history", label: "Historia globalna", path: "/history", icon: History, keywords: ["historia", "ostatnie"] },
  { id: "settings", label: "Ustawienia", path: "/settings", icon: Settings, keywords: ["ustawienia", "profil", "konto", "settings"] },
];

const MAX_POPULAR = 6;

function getRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentSearch(query) {
  if (!query.trim()) return;
  const recent = getRecentSearches().filter((q) => q !== query.trim());
  recent.unshift(query.trim());
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searchError, setSearchError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Load recent searches on open
  useEffect(() => {
    if (open) {
      setRecentSearches(getRecentSearches());
      setQuery("");
      setResults(null);
      setSearchError(false);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setResults(null);
      setSearchError(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setSearchError(false);
    try {
      const res = await globalSearch(searchQuery.trim());
      setResults(res.data);
      setSelectedIndex(0);
    } catch {
      // Nie udajemy "brak wynikow" gdy wyszukiwanie sie nie powiodlo —
      // odrozniamy blad od pustego wyniku (reguła UX 5).
      setResults(null);
      setSearchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim() || query.trim().length < 2) {
      setResults(null);
      setSearchError(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, performSearch]);

  // ---- Komendy nawigacyjne: dopasowanie po frazie ----
  const q = query.trim().toLowerCase();
  const matchedCommands = q
    ? NAV_COMMANDS.filter(
        (c) =>
          c.label.toLowerCase().includes(q) ||
          c.keywords.some((k) => k.includes(q) || q.includes(k))
      ).slice(0, 8)
    : NAV_COMMANDS.filter((c) => c.popular).slice(0, MAX_POPULAR);

  // Flatten for keyboard navigation: KOMENDY najpierw, potem wyniki search
  const flatItems = [];
  matchedCommands.forEach((c) => flatItems.push({ ...c, _kind: "command" }));
  if (results) {
    for (const type of ["project", "source", "knowledge"]) {
      const items = results[type] || results[`${type}s`] || [];
      items.forEach((item) => flatItems.push({ ...item, _kind: "result", _type: type }));
    }
  }

  const goToItem = (item) => {
    if (!item) return;
    if (item._kind === "command") {
      setOpen(false);
      navigate(item.path);
      return;
    }
    // wynik wyszukiwania
    saveRecentSearch(query);
    setOpen(false);
    const type = item._type || item.type;
    if (type === "project") {
      navigate(`/project/${item.id}/creator`);
    } else if (type === "source") {
      navigate(`/project/${item.project_id}/sources`);
    } else if (type === "knowledge") {
      navigate("/knowledge-base");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flatItems.length > 0 && flatItems[selectedIndex]) {
        goToItem(flatItems[selectedIndex]);
      }
    }
  };

  const handleRecentClick = (recentQuery) => {
    setQuery(recentQuery);
  };

  // Group search results by type for display
  const groupedResults = {};
  if (results) {
    for (const type of ["project", "source", "knowledge"]) {
      const items = results[type] || results[`${type}s`] || [];
      if (items.length > 0) {
        groupedResults[type] = items.map((item) => ({ ...item, _type: type }));
      }
    }
  }

  // globalIndex liczy od liczby komend (komendy zajmuja pierwsze indeksy)
  let globalIndex = matchedCommands.length;
  const hasSearchResults = Object.keys(groupedResults).length > 0;

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-muted/50 text-sm text-muted-foreground hover:bg-muted transition-colors"
      >
        <Command className="h-4 w-4" />
        <span className="hidden sm:inline">Co chcesz zrobić?</span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border bg-background px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
          <Command className="h-3 w-3" />K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b">
            <Command className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
              onKeyDown={handleKeyDown}
              placeholder="Przejdź do modułu lub szukaj projektów, źródeł, wiedzy..."
              className="h-12 border-0 shadow-none focus-visible:ring-0 px-0 text-sm"
            />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />}
            {query && !loading && (
              <button onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <ScrollArea className="max-h-[420px]">
            {/* KOMENDY / Przejdz do — zawsze gdy sa dopasowania */}
            {matchedCommands.length > 0 && (
              <div className="p-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 py-2">
                  {q ? "Przejdź do" : "Szybki dostęp"}
                </p>
                {matchedCommands.map((cmd, i) => {
                  const isSelected = i === selectedIndex;
                  const CmdIcon = cmd.icon;
                  return (
                    <button
                      key={cmd.id}
                      onClick={() => goToItem({ ...cmd, _kind: "command" })}
                      onMouseEnter={() => setSelectedIndex(i)}
                      className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm transition-colors ${
                        isSelected ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-muted"
                      }`}
                    >
                      <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 text-gold bg-gold/10">
                        <CmdIcon className="h-4 w-4" />
                      </div>
                      <span className="flex-1 text-left font-medium">{cmd.label}</span>
                      {isSelected && <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Recent searches (gdy brak query i sa historie) */}
            {!query.trim() && recentSearches.length > 0 && (
              <div className="p-2 pt-0">
                <Separator className="my-1" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 py-2">
                  Ostatnie wyszukiwania
                </p>
                {recentSearches.map((recent, i) => (
                  <button
                    key={i}
                    onClick={() => handleRecentClick(recent)}
                    className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    <Clock className="h-4 w-4 shrink-0" />
                    <span className="truncate text-left">{recent}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Brak dopasowania komend i brak query */}
            {!query.trim() && matchedCommands.length === 0 && recentSearches.length === 0 && (
              <div className="p-8 text-center">
                <Command className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Wpisz cel (np. „umowa", „kontrahent", „porada") lub szukaj treści
                </p>
              </div>
            )}

            {/* Wyniki wyszukiwania */}
            {hasSearchResults && (
              <div className="p-2 pt-0">
                <Separator className="my-1" />
                {Object.entries(groupedResults).map(([type, items]) => {
                  const config = typeConfig[type] || typeConfig.project;
                  const TypeIcon = config.icon;
                  return (
                    <div key={type}>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 py-2">
                        {config.label}
                      </p>
                      {items.map((item) => {
                        const currentIndex = globalIndex++;
                        const isSelected = currentIndex === selectedIndex;
                        return (
                          <button
                            key={`${type}-${item.id || currentIndex}`}
                            onClick={() => goToItem({ ...item, _kind: "result" })}
                            onMouseEnter={() => setSelectedIndex(currentIndex)}
                            className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm transition-colors ${
                              isSelected ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-muted"
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${config.color}`}>
                              <TypeIcon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <p className="font-medium text-sm truncate">{item.name || item.title}</p>
                              {item.description && (
                                <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                              )}
                            </div>
                            <Badge variant="outline" className="text-[10px] shrink-0">
                              {config.label}
                            </Badge>
                            {isSelected && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Szukano tresci, brak wynikow (ale komendy moga byc wyzej) */}
            {query.trim().length >= 2 && !loading && !hasSearchResults && results !== null && (
              <div className="px-4 py-3 text-center">
                <p className="text-xs text-muted-foreground">Brak treści dla „{query}" — spróbuj innej frazy</p>
              </div>
            )}

            {/* Wyszukiwanie tresci sie nie powiodlo — osobny stan bledu (nie "brak wynikow").
                Uczciwy komunikat (chwilowy problem po naszej stronie) + jawny retry. */}
            {query.trim().length >= 2 && !loading && searchError && (
              <div className="px-4 py-4 text-center">
                <p className="text-xs text-destructive mb-2">
                  Nie udało się wyszukać treści — to chwilowy problem po naszej stronie.
                </p>
                <button
                  onClick={() => performSearch(query)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                >
                  <Loader2 className="h-3.5 w-3.5" /> Spróbuj ponownie
                </button>
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t bg-muted/30 text-[10px] text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-background px-1 py-0.5 font-mono">&#8593;&#8595;</kbd> nawigacja
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-background px-1 py-0.5 font-mono">Enter</kbd> otwórz
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-background px-1 py-0.5 font-mono">Esc</kbd> zamknij
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
